import React, { useState, useEffect, useCallback } from 'react';

// All seed content (defaults / SEO FAQ pack) lives in the backend service
// `landing_settings_service.DEFAULTS` and is fetched via `/api/admin/landing/defaults`.
// No content is hardcoded in this file.

/**
 * LandingPageAdmin
 * ────────────────
 * Edits the singleton settings doc that drives the public SSR landing page.
 *
 * Source of truth split:
 *   - Plan content (price/features/etc)  →  existing Membership admin
 *   - Everything else on the landing     →  this tab
 *
 * Endpoints: GET / PUT /api/admin/landing  (admin token required)
 */
const LandingPageAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const cardCls = `rounded-xl border p-5 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;
  const labelCls = `block text-xs font-semibold uppercase tracking-wider mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`;
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const sectionTitleCls = `text-lg font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch(`${API}/admin/landing`, { headers: { Authorization: `Bearer ${adminToken}` } });
      if (!r.ok) {
        const body = await r.text().catch(() => '');
        throw new Error(`HTTP ${r.status} ${r.statusText}${body ? ` — ${body.slice(0, 200)}` : ''}`);
      }
      setData(await r.json());
    } catch (e) {
      showNotification?.(`Failed to load landing settings: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [API, adminToken, showNotification]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!data) return;
    try {
      setSaving(true);
      const r = await fetch(`${API}/admin/landing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify(data),
      });
      if (!r.ok) throw new Error(await r.text());
      const fresh = await r.json();
      setData(fresh);
      showNotification?.('Landing page saved. Reload the public page to see changes.', 'success');
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const update = (key, value) => setData((d) => ({ ...d, [key]: value }));
  const updateStatItem = (idx, key, value) => setData((d) => {
    const items = [...(d.stats_items || [])];
    items[idx] = { ...items[idx], [key]: value };
    return { ...d, stats_items: items };
  });
  const addFaq = () => setData((d) => ({ ...d, faqs: [...(d.faqs || []), { q: '', a: '' }] }));
  const updateFaq = (idx, key, value) => setData((d) => {
    const faqs = [...(d.faqs || [])];
    faqs[idx] = { ...faqs[idx], [key]: value };
    return { ...d, faqs };
  });
  const removeFaq = (idx) => setData((d) => ({ ...d, faqs: (d.faqs || []).filter((_, i) => i !== idx) }));
  const addPair = () => setData((d) => ({ ...d, samples_pairs: [...(d.samples_pairs || []), { en: '', darija: '' }] }));
  const updatePair = (idx, key, value) => setData((d) => {
    const pairs = [...(d.samples_pairs || [])];
    pairs[idx] = { ...pairs[idx], [key]: value };
    return { ...d, samples_pairs: pairs };
  });
  const removePair = (idx) => setData((d) => ({ ...d, samples_pairs: (d.samples_pairs || []).filter((_, i) => i !== idx) }));

  if (loading || !data) {
    return <div className={`p-8 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading landing page settings…</div>;
  }

  const Toggle = ({ k, label }) => (
    <label className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
      <span className="text-sm">{label}</span>
      <input
        type="checkbox"
        checked={!!data[k]}
        onChange={(e) => update(k, e.target.checked)}
        className="w-4 h-4"
      />
    </label>
  );

  return (
    <div className="space-y-6 max-w-5xl">
      <div className="flex items-center justify-between sticky top-0 z-10 py-3 -mx-6 px-6 backdrop-blur" style={{ background: isDarkMode ? 'rgba(17,24,39,0.85)' : 'rgba(255,255,255,0.85)' }}>
        <div>
          <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Landing Page</h2>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Edit copy, sections, and FAQs shown on the public homepage.</p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold shadow disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

      {/* PRE-LAUNCH BANNER */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Pre-launch Banner</h3>
        <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Thin bar above the nav on the public homepage. Use it to tell visitors the site isn't launched yet.
          Turn it off once you go live.
        </p>
        <div className="space-y-3">
          <Toggle k="prelaunch_banner_show" label="Show banner on homepage" />
          <div>
            <label className={labelCls}>Banner text</label>
            <input
              className={inputCls}
              value={data.prelaunch_banner_text || ''}
              onChange={(e) => update('prelaunch_banner_text', e.target.value)}
              placeholder="✨ Pre-launch. We're putting the final brushstrokes on something good. Come back soon. Salam from the team."
            />
          </div>
          <Toggle k="prelaunch_banner_timer_show" label="Show countdown timer next to text" />
          <div>
            <label className={labelCls}>Launch date &amp; time (local)</label>
            <input
              type="datetime-local"
              className={inputCls}
              value={(data.prelaunch_banner_launch_at || '').slice(0, 16)}
              onChange={(e) => update('prelaunch_banner_launch_at', e.target.value)}
            />
            <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              When the countdown hits zero, the banner shows "Launching now". Leave blank to hide the timer.
            </p>
          </div>
        </div>
      </div>

      {/* HERO */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Hero</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className={labelCls}>Eyebrow (small text above headline)</label>
            <input className={inputCls} value={data.hero_eyebrow || ''} onChange={(e) => update('hero_eyebrow', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>H1 — part 1 (before highlight)</label>
            <input className={inputCls} value={data.hero_h1_part1 || ''} onChange={(e) => update('hero_h1_part1', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>H1 — gradient highlight word</label>
            <input className={inputCls} value={data.hero_h1_highlight || ''} onChange={(e) => update('hero_h1_highlight', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>H1 — part 2 (after highlight)</label>
            <input className={inputCls} value={data.hero_h1_part2 || ''} onChange={(e) => update('hero_h1_part2', e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className={labelCls}>Subtitle</label>
            <textarea rows={3} className={inputCls} value={data.hero_subtitle || ''} onChange={(e) => update('hero_subtitle', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Primary CTA — label</label>
            <input className={inputCls} value={data.hero_cta_primary_label || ''} onChange={(e) => update('hero_cta_primary_label', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Primary CTA — URL</label>
            <input className={inputCls} value={data.hero_cta_primary_href || ''} onChange={(e) => update('hero_cta_primary_href', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Secondary CTA — label</label>
            <input className={inputCls} value={data.hero_cta_secondary_label || ''} onChange={(e) => update('hero_cta_secondary_label', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Secondary CTA — URL</label>
            <input className={inputCls} value={data.hero_cta_secondary_href || ''} onChange={(e) => update('hero_cta_secondary_href', e.target.value)} />
          </div>
        </div>
        <h4 className={`text-sm font-bold mt-6 mb-3 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Hero stats (4 inline)</h4>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((n) => (
            <div key={n} className={`rounded-lg border p-3 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <input className={`${inputCls} mb-2`} placeholder="Value (e.g. 12,500+)" value={data[`hero_stat_${n}_value`] || ''} onChange={(e) => update(`hero_stat_${n}_value`, e.target.value)} />
              <input className={inputCls} placeholder="Label (e.g. verified words)" value={data[`hero_stat_${n}_label`] || ''} onChange={(e) => update(`hero_stat_${n}_label`, e.target.value)} />
            </div>
          ))}
        </div>
      </div>

      {/* SECTION TOGGLES */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Show / hide sections</h3>
        <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Toggle off any section to hide it on the public page (Hero is always shown).</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Toggle k="show_live_morocco"   label="Right now in Morocco (live time + cities)" />
          <Toggle k="show_dictionary"     label="Dictionary spotlight" />
          <Toggle k="show_translation"    label="Tools — Translation card" />
          <Toggle k="show_decoder"        label="Tools — Decoder card" />
          <Toggle k="show_verb_explorer"  label="Tools — Verb Explorer card" />
          <Toggle k="show_music"          label="Music section" />
          <Toggle k="show_games"          label="Games section" />
          <Toggle k="show_travel_tools"     label="Travel Tools" />
          <Toggle k="show_make_your_own"  label="Make Your Own Dictionary" />
          <Toggle k="show_daily_practice" label="Daily Practice" />
          <Toggle k="show_holidays"       label="Holidays" />
          <Toggle k="show_pricing"        label="Pricing cards" />
          <Toggle k="show_faq"            label="FAQ" />
        </div>
      </div>

      {/* PRICING SECTION COPY */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Pricing section</h3>
        <p className={`text-xs mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>Plan content (price, features, popular badge) is edited in the existing <strong>Membership</strong> tab. Only the section header lives here.</p>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className={labelCls}>Pricing section title</label>
            <input className={inputCls} value={data.pricing_title || ''} onChange={(e) => update('pricing_title', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Pricing section subtitle</label>
            <input className={inputCls} value={data.pricing_subtitle || ''} onChange={(e) => update('pricing_subtitle', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>"Compare all features" link label</label>
            <input className={inputCls} value={data.pricing_compare_label || ''} onChange={(e) => update('pricing_compare_label', e.target.value)} />
          </div>
        </div>
      </div>

      {/* FAQS */}
      <div className={cardCls}>
        <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
          <h3 className={sectionTitleCls + ' mb-0'}>FAQ</h3>
          <button onClick={addFaq} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold">+ Add FAQ</button>
        </div>
        <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          FAQs render at the bottom of the home page and feed Google's <code>FAQPage</code> rich-result schema. Aim for 8–15 keyword-rich questions for best SEO.
        </p>
        {(data.faqs || []).length === 0 && (
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>No FAQs yet. Click "+ Add FAQ".</p>
        )}
        <div className="space-y-4">
          {(data.faqs || []).map((f, i) => (
            <div key={i} className={`rounded-lg border p-4 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <span className={`text-xs font-bold uppercase tracking-wider ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>FAQ #{i + 1}</span>
                <button onClick={() => removeFaq(i)} className="text-red-500 text-xl leading-none px-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded" title="Remove">×</button>
              </div>
              <label className={labelCls}>Question</label>
              <input
                className={`${inputCls} font-semibold mb-3`}
                placeholder="e.g. What is Moroccan Darija?"
                value={f.q || ''}
                onChange={(e) => updateFaq(i, 'q', e.target.value)}
              />
              <label className={labelCls}>Answer</label>
              <textarea
                rows={3}
                className={inputCls}
                placeholder="Write a clear, helpful answer (1–3 sentences)…"
                value={f.a || ''}
                onChange={(e) => updateFaq(i, 'a', e.target.value)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* SAMPLE TRANSLATIONS */}
      <div className={cardCls}>
        <div className="flex items-center justify-between mb-4">
          <h3 className={sectionTitleCls + ' mb-0'}>Sample translations strip</h3>
          <button onClick={addPair} className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-sm font-semibold">+ Add pair</button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(data.samples_pairs || []).map((p, i) => (
            <div key={i} className={`flex items-center gap-2 rounded-lg border p-2 ${isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <input className={inputCls} placeholder="English" value={p.en || ''} onChange={(e) => updatePair(i, 'en', e.target.value)} />
              <input className={inputCls} placeholder="Darija" value={p.darija || ''} onChange={(e) => updatePair(i, 'darija', e.target.value)} />
              <button onClick={() => removePair(i)} className="text-red-500 text-xl leading-none px-2" title="Remove">×</button>
            </div>
          ))}
        </div>
      </div>

      {/* HERO DEMO MOCKUP TEXT */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Hero demo card (visual only)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>English input shown</label>
            <input className={inputCls} value={data.hero_demo_input || ''} onChange={(e) => update('hero_demo_input', e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Darija output shown</label>
            <input className={inputCls} value={data.hero_demo_output || ''} onChange={(e) => update('hero_demo_output', e.target.value)} />
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <div className={cardCls}>
        <h3 className={sectionTitleCls}>Footer</h3>
        <div>
          <label className={labelCls}>Footer tagline</label>
          <input className={inputCls} value={data.footer_tagline || ''} onChange={(e) => update('footer_tagline', e.target.value)} />
        </div>
      </div>

      {/* Save again at bottom for long forms */}
      <div className="flex justify-end pb-10">
        <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold shadow disabled:opacity-50">
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>

    </div>
  );
};

export default LandingPageAdmin;
